import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { App } from '@df/core/app';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { KlevuSearch } from '@df/module-klevu-search/klevu-interfaces';
import { KlevuSearchService } from '@df/module-klevu-search/klevu-search.service';
import { KlevuTrackService } from '@df/module-klevu-search/klevu-track.service';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { UiComponent } from '@df/ui/ui.component';
import head from 'lodash-es/head';
import isNull from 'lodash-es/isNull';

@Component({
    selector: 'search-banner',
    templateUrl: './search-banner.component.html',
    standalone: true,
    imports: [CommonModule, DfImageDirective, HrefComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchBannerComponent extends UiComponent implements OnInit, OnChanges {
    @Input()
    term?: string;

    @Input()
    showOnQuickSearch = false;

    @Input()
    showOnCategoryPage = false;

    @Input()
    position?: 'top' | 'bottom';

    currentBanner?: KlevuSearch.IBanner;

    banners?: KlevuSearch.IBanner[];

    searchService = inject(KlevuSearchService);
    protected klevuTrackService = inject(KlevuTrackService);
    readonly app = inject(App);

    override ngOnInit(): void {
        super.ngOnInit();

        this.searchService.loadKlevuBanners().then(() => {
            this.banners = this.searchService.getEligibleBanners();

            this.onTermChange();
        });
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if (changes['term'] && this.banners?.length) {
            this.onTermChange();
        }

        super.ngOnChanges(changes);
    }

    private onTermChange(): void {
        const term = this.term?.toLowerCase();

        if (term && this.banners?.length) {
            const device = this.client.isM || this.client.isS ? 'mobile' : 'desktop';

            // first we search banners that match the term or match all `null`
            const banners = this.banners
                .filter(banner => {
                    if (banner?.showForTerms?.length) {
                        return !!banner.showForTerms.find(i => i.toLowerCase() === term);
                    }

                    return isNull(banner?.showForTerms);
                })
                .filter(banner => {
                    // banner name should include the device name
                    return !!banner?.bannerName?.toLowerCase().includes(device);
                })
                .filter(banner => {
                    // then we search banners that match the position
                    if (this.showOnQuickSearch) {
                        if (this.position) {
                            return banner?.showOnQuickSearch && banner?.position === this.position;
                        }

                        return banner?.showOnQuickSearch;
                    }

                    if (this.showOnCategoryPage) {
                        return banner?.showOnCategoryPage || banner?.showOnLandingPage;
                    }

                    return false;
                });

            this.currentBanner = head(banners);
        } else {
            this.currentBanner = undefined;
        }

        this.detectChanges();
    }

    trackClick() {
        this.klevuTrackService.trackBanner(this.currentBanner, this.term);
    }

    get isSameDomain(): boolean {
        const storeUrl = this.app.url.storeUrl;
        const url =
            storeUrl.indexOf(this.app.multisite.current.storeCode) !== -1
                ? storeUrl.replace(this.app.multisite.current.storeCode + '/', '')
                : storeUrl;

        return !!this.currentBanner?.redirectUrl?.includes(url);
    }

    get redirectLink(): string | undefined {
        const redirectUrl = this.currentBanner?.redirectUrl;

        if (this.isSameDomain && redirectUrl) {
            return new URL(redirectUrl).pathname;
        }

        return redirectUrl;
    }

    get urlTarget(): string | undefined {
        if (!this.isSameDomain) {
            return '_blank';
        }

        return undefined;
    }
}
